import { localizedStrings } from '@core/strings'
import React, { useState } from 'react'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LoadingButton,
  TextField,
} from '@common/material'
import { Box, Text } from '@fundamentals'
import ClearIcon from '@mui/icons-material/Clear'
import { useModal } from '@common/GlobalModal'
import { SignatureEdit } from 'webapp/components/common/Signature'
import useConstants from '@core/providers/useConstants'
import { AttachmentInput } from '@elements/Questions/AttachmentInput'
import { AttachmentTypes } from '@core/components/templateAnswersForm'
import { useUploadMedia } from '@core/react-query/features/media'
import BackdropLoader from '@common/fundamentals/BackdropLoader'
import {
  ResumePermitSubmitData,
  SuspendPermitSubmitData,
} from '@modules/permitDetails/types'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { Permit } from '@core/entities/permit/Permit'
import { ModalDialogContainer } from '@common/modal'
import { ProofType } from '@core/entities/template/TemplateQuestion'
import { useIsMutating } from '@tanstack/react-query'
import { permitKeys } from '@core/react-query/features/permits/permit'

type ComponentType = {
  // we keep the old type here for now, until the full permit details refactor is done
  permit: Permit
  onSubmit?: (data: SuspendPermitSubmitData | ResumePermitSubmitData) => void
}

type ProofProps = {
  url: string
  proof: File
  type?: ProofType
}

export const SuspendResumePermitModal: React.FC<ComponentType> = ({
  permit,
  onSubmit,
}) => {
  const [reason, setReason] = useState<string>('')
  const [files, setFiles] = useState<ProofProps[]>([])
  const { hideModal } = useModal()
  const { constants } = useConstants()
  const { data: profile } = useGetProfile()
  const uploadMedia = useUploadMedia()

  const isSubmittingResumePermit = useIsMutating({
    mutationKey: permitKeys.resumePermit.mutationKey,
  })

  const isSubmittingRequestResumePermit = useIsMutating({
    mutationKey: permitKeys.requestResumePermit.mutationKey,
  })

  const isSubmittingSuspendPermit = useIsMutating({
    mutationKey: permitKeys.suspendPermit.mutationKey,
  })

  const handleProofsChange = (proofs: Array<File & { uri?: string }>) => {
    setFiles(
      proofs.map((p) => ({
        proof: p as File,
        url: p.uri ?? URL.createObjectURL(p),
        type: p.type?.includes('pdf')
          ? ('pdf' as ProofType)
          : ('img' as ProofType),
      })),
    )
  }

  const handleSubmit = async () => {
    const uploadedProofs = await Promise.all(
      files.map(async (file: any) => {
        if (!file.downloadUrl) {
          const uploadedFileKey = await uploadMedia.mutateAsync({
            file: file.proof,
            folderName: `${permit.template.id}-${
              file.type === 'pdf' ? 'documents' : 'proof'
            }`,
          })

          return { ...file, key: uploadedFileKey }
        }
        return file
      }),
    )

    const attachmentsKeys = uploadedProofs.map((file) => file.key) || []

    if (permit.status === 'SUSPENDED') {
      onSubmit({ comment: reason, attachmentsKeys })
    } else {
      onSubmit({
        comment: reason,
        attachmentsKeys,
        // we'll keep suspensionReasonCategory as GENERIC for now. stage 2 we will add a dropdown to select the reason
        suspensionReasonCategory: 'GENERIC',
      })
    }
  }

  return (
    <ModalDialogContainer>
      <BackdropLoader open={uploadMedia.isLoading} />
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {permit.status === 'SUSPENDED'
          ? localizedStrings.resumePermit
          : localizedStrings.suspendPermit}
        <IconButton aria-label='close-modal' onClick={hideModal}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <TextField
          name='reason'
          label={
            permit.status === 'SUSPENDED'
              ? localizedStrings.resumePermitReason
              : localizedStrings.suspendPermitReason
          }
          value={reason}
          multiline
          rows={3}
          required
          sx={{ width: '100%', mt: 1, mb: 2 }}
          placeholder={localizedStrings.enterReason}
          onChange={(e) => setReason(Utils.safeParseEventValue(e))}
        />
        <Box sx={{ mb: 2 }}>
          <AttachmentInput
            inputProps={{
              // spreading a File drops its bytes, so pass the File itself
              value: files.map((f) => f.proof),
              onChange: handleProofsChange,
            }}
            attachmentTypes={[AttachmentTypes.IMAGE, AttachmentTypes.PDF]}
            dataTest='suspend-resume-attachment-input'
            maxFiles={constants.max_number_of_proofs}
            maxFileSize={constants.max_question_attachment_size_in_bytes}
            readOnly={files.length >= constants.max_number_of_proofs}
          />
        </Box>
        <Text sx={{ fontSize: 12, mt: 3 }} color='text.secondary' gutterBottom>
          {permit.status === 'SUSPENDED'
            ? localizedStrings.resumePermitDeclaration
            : localizedStrings.suspendPermitDeclaration}
        </Text>
        <SignatureEdit readonly value={profile?.signatureUrl} />
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button variant='outlined' onClick={hideModal}>
          {localizedStrings.cancel}
        </Button>
        <LoadingButton
          variant='contained'
          disabled={!reason?.length}
          loading={
            !!isSubmittingResumePermit ||
            !!isSubmittingRequestResumePermit ||
            !!isSubmittingSuspendPermit
          }
          onClick={handleSubmit}
          data-test='suspend-resume-submit'
        >
          {localizedStrings.submit}
        </LoadingButton>
      </DialogActions>
    </ModalDialogContainer>
  )
}
